"use client";

import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNotifications } from "../contexts/NotificationContext";
import type { Notification } from "../contexts/NotificationContext";
import { ClockWidget, CalendarWidget, SystemStatsWidget } from "./WidgetLayer";

/* ==========================================================================
   HELPERS
   ========================================================================== */

function getTypeColor(type: Notification["type"]): string {
  switch (type) {
    case "success":
      return "#34C759";
    case "warning":
      return "#FF9500";
    case "error":
      return "#FF3B30";
    case "info":
    default:
      return "#007AFF";
  }
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/* ==========================================================================
   GLASS CARD — frosted glass wrapper for widgets & notification list
   ========================================================================== */

function GlassCard({
  isDark,
  children,
  style,
}: {
  isDark: boolean;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        borderRadius: 16,
        backgroundColor: isDark
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(255, 255, 255, 0.18)",
        backdropFilter: "blur(60px) saturate(1.4)",
        WebkitBackdropFilter: "blur(60px) saturate(1.4)",
        border: isDark
          ? "1px solid rgba(255, 255, 255, 0.12)"
          : "1px solid rgba(255, 255, 255, 0.35)",
        boxShadow: isDark
          ? "0 2px 16px rgba(0,0,0,0.2), inset 0 0.5px 0 rgba(255,255,255,0.05)"
          : "0 2px 16px rgba(0,0,0,0.06), inset 0 0.5px 0 rgba(255,255,255,0.4)",
        overflow: "hidden",
        pointerEvents: "auto",
        animation: "nwp-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* ==========================================================================
   WIDGET CARD — glass widget with title header
   ========================================================================== */

function WidgetCard({
  title,
  height,
  isDark,
  children,
}: {
  title: string;
  height: number;
  isDark: boolean;
  children: React.ReactNode;
}) {
  const headerColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)";
  const borderColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";

  return (
    <GlassCard isDark={isDark} style={{ height }}>
      <div
        style={{
          padding: "6px 12px",
          borderBottom: `1px solid ${borderColor}`,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: headerColor,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {title}
        </span>
      </div>
      <div style={{ height: height - 30, overflow: "hidden" }}>{children}</div>
    </GlassCard>
  );
}

/* ==========================================================================
   NOTIFICATION WIDGET PANEL
   ========================================================================== */

export function NotificationWidgetPanel({
  isOpen,
  onClose,
  isDark,
}: {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
}) {
  const {
    notifications,
    dismissNotification,
    markAsRead,
    markAllAsRead,
    clearAll,
  } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close only when the user clicks the desktop background (wallpaper),
  // not on every click outside the panel — interacting with apps, the
  // menubar, or the dock shouldn't dismiss the panel.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (target.closest('[data-desktop-background="true"]')) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handler);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handler);
    };
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const handleNotificationClick = useCallback(
    (notif: Notification) => {
      markAsRead(notif.id);
      notif.onClick?.();
    },
    [markAsRead]
  );

  if (!isOpen) return null;

  const textPrimary = isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)";
  const textSecondary = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)";
  const textTertiary = isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)";

  return createPortal(
    <>
      {/* Panel container — pure layout, no background, no compositing */}
      <div
        ref={panelRef}
        style={{
          position: "fixed",
          top: 36,
          right: 8,
          bottom: 8,
          width: 340,
          zIndex: 150,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 4,
          pointerEvents: "none",
        }}
      >
        {/* ── Notifications Card ── */}
        <GlassCard isDark={isDark}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: isDark
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: textPrimary,
              }}
            >
              Notifications
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              {notifications.length > 0 && (
                <>
                  <button
                    onClick={markAllAsRead}
                    style={{
                      fontSize: 11,
                      color: "#007AFF",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px 4px",
                    }}
                  >
                    Mark all read
                  </button>
                  <button
                    onClick={clearAll}
                    style={{
                      fontSize: 11,
                      color: textSecondary,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "2px 4px",
                    }}
                  >
                    Clear all
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Notification list */}
          <div style={{ maxHeight: 280, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  padding: "24px 14px",
                  textAlign: "center",
                  fontSize: 12,
                  color: textTertiary,
                }}
              >
                No notifications
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleNotificationClick(notif)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 14px",
                    cursor: notif.onClick ? "pointer" : "default",
                    backgroundColor: !notif.read
                      ? isDark
                        ? "rgba(0, 122, 255, 0.1)"
                        : "rgba(0, 122, 255, 0.06)"
                      : "transparent",
                    transition: "background-color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    if (notif.read) {
                      e.currentTarget.style.backgroundColor = isDark
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.03)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = !notif.read
                      ? isDark
                        ? "rgba(0, 122, 255, 0.1)"
                        : "rgba(0, 122, 255, 0.06)"
                      : "transparent";
                  }}
                >
                  {notif.icon ? (
                    <div style={{ flexShrink: 0, marginTop: 2 }}>
                      {notif.icon}
                    </div>
                  ) : (
                    <div
                      style={{
                        flexShrink: 0,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        marginTop: 6,
                        backgroundColor: getTypeColor(notif.type),
                      }}
                    />
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {notif.appName && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 500,
                              color: textSecondary,
                              textTransform: "uppercase",
                              letterSpacing: "0.03em",
                            }}
                          >
                            {notif.appName}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: notif.read ? 400 : 600,
                            color: textPrimary,
                          }}
                        >
                          {notif.title}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: 10,
                          color: textTertiary,
                          flexShrink: 0,
                        }}
                      >
                        {formatTimeAgo(notif.timestamp)}
                      </span>
                    </div>
                    {notif.message && (
                      <div
                        style={{
                          fontSize: 12,
                          color: textSecondary,
                          marginTop: 2,
                          lineHeight: 1.4,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {notif.message}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      dismissNotification(notif.id);
                    }}
                    style={{
                      flexShrink: 0,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "none",
                      backgroundColor: "transparent",
                      color: textTertiary,
                      fontSize: 13,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                      padding: 0,
                      opacity: 0.6,
                      transition: "opacity 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "0.6";
                    }}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </GlassCard>

        {/* ── Widgets ── */}
        <WidgetCard title="Clock" height={260} isDark={isDark}>
          <ClockWidget onActivate={onClose} />
        </WidgetCard>
        <WidgetCard title="Calendar" height={260} isDark={isDark}>
          <CalendarWidget onActivate={onClose} />
        </WidgetCard>
        <WidgetCard title="System" height={180} isDark={isDark}>
          <SystemStatsWidget onActivate={onClose} />
        </WidgetCard>
      </div>

      <style>{`
        @keyframes nwp-slide-in {
          from {
            opacity: 0;
            transform: translateX(60px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>,
    document.body
  );
}
